import { Connector } from './types';

/**
 * Re-read the chosen connector's status from a fresh station snapshot.
 *
 * `chosenConnector` is a *copy* taken when the connector was picked, so
 * refreshing `station` on its own never moves the screen: the driver stays on
 * "insert the connector" even though REST already reports `Preparing`. Every
 * path that reconciles against REST — the manual refresh and the post-reconnect
 * rehydrate — runs the chosen connector back through here.
 *
 * Returns `prev` itself when there is nothing to change, so it is safe to call
 * from a `setState` updater without churning references.
 *
 * @param prev `false` before the first load, `null` while on the connector
 *   picker, otherwise the connector the driver is looking at.
 */
export const resolveChosenConnector = (
  prev: Connector | null | false,
  connectors: Connector[],
): Connector | null | false => {
  if (!prev) return prev;

  const fresh = connectors.find((c) => c.connectorId === prev.connectorId);
  // A connector missing from the snapshot is not the same as a connector that
  // went free — keep showing what we have rather than bouncing to the picker.
  if (!fresh || fresh.status === prev.status) return prev;

  // Spread `prev` last-wins only on status: the snapshot carries no sessionId,
  // and dropping it would break the "continue your charge" branch.
  return { ...prev, status: fresh.status };
};
